import React, { useState } from "react"
import toast from "react-hot-toast"
import { Col, Row } from "reactstrap"
import { useFormik } from "formik"

import {
   CreateWarranty,
   DeleteWarranty,
   GetWarrantyListing,
   UpdateWarranty,
   UpdateWarrantyListingSequence,
} from "../../../api/api.service"
import { AutoComplete } from "../../../components/AutoComplete"
import { Button } from "../../../components/Button"
import { DataTable } from "../../../components/DataTable"
import { SearchBox } from "../../../components/SearchBox"
import ActiveInactiveSwitch from "../../Configurator/CommonUI/ActiveInactiveSwitch/ActiveInactiveSwitch"
import DeleteModal from "../../Configurator/CommonUI/Modal/DeleteModal"
import { useLocalPaginatedRequest } from "../../../hooks/useLocalPaginatedRequest"
import { DragDropContext, Droppable } from "react-beautiful-dnd"
import { ConfiguratorPackagesStatus } from "../../../constants"
import { convertToSelectOptions } from "../../../utils/commonUtils"

import eyeIcon from "../../../assets/images/icons/eye-icon.svg"
import DeleteIcon from "assets/images/icons/finance-vendor-mang-del-icon.svg"
import editIcon from "../../../assets/images/icons/edit-icon.svg"

import {
   FIELDS_NAME,
   initialValues,
   validationSchema,
   TOGGLE_STATUS,
} from "./GlobalWarrantyFormConfig"
import ManageGlobalWarranties from "./ManageGlobalWarranties"
import GlobalWarrantyDetails from "./GlobalWarrantyDetails"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"
import { Link } from "react-router-dom"
import { APP_ROUTES } from "helpers/routeHelpers"
import useRenderUsedIn from "hooks/useRenderUsedIn"
import TruncatedTextWithTooltip from "components/CustomTooltip/CustomTooltip"

const COLUMNS = {
   WARRANTY_NAME: "Warranty Name",
   WARRANTY_YEAR: "Year",
   WARRANTY_PRICE: "Price",
   STATUS: "Status",
   ACTIONS: "Actions",
   DEFAULT: "Default",
   POPULAR_WARRANTY: "Popular Warranty",
   USED_IN: "Used In",
}
const DATA_KEY = "data"
const TOTAL_KEY = "total"
const handleUpdateExecution = async (id, data) => {
   let res = await UpdateWarranty(id, data)
   toast.success("Warranty has been updated successfully !")
}
const GlobalWarranty = ({}) => {
   const [statusFilters, setStatusFilters] = useState(null)
   const [selection, setSelection] = useState({})
   const [isLoading, setIsLoading] = useState(false)
   const [eventsType, setEventsType] = useState({
      isManage: null,
      isViewDetails: null,
      isDelete: null,
   })

   const {
      data,
      isFetching,
      searchText,
      handleSearchChange,
      request,
      setTableDataChangeLoading,
      tableDataChangeLoading,
      setData,
   } = useLocalPaginatedRequest({
      requestFn: GetWarrantyListing,
      customParams: {
         column: "sequenceNo",
         limit: -1,
         page: 1,
         ...(statusFilters?.value && { status: statusFilters?.value }),
      },
      searchInCustomParams: true,
      deps: [statusFilters],
      dataKey: DATA_KEY,
      totalKey: TOTAL_KEY,
   })

   const handleDragEnd = async result => {
      // A filtered/searched list is a subset; the reorder endpoint needs every row, so block it.
      if (statusFilters?.value || searchText?.trim()) return
      if (!result.destination) return
      if (result.destination.index === result.source.index) return
      const previous = Array.from(data)
      const items = Array.from(data)
      const [moved] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, moved)
      setData(items)
      try {
         await UpdateWarrantyListingSequence({
            sequenceItems: items.map((item, index) => ({
               id: Number(item.id),
               sequenceNo: index,
            })),
         })
         toast.success("Global warranty order updated.")
      } catch (err) {
         setData(previous)
         toast.error("Failed to update global warranty order")
      }
   }

   const renderUsedIn = useRenderUsedIn({
      listKey: "configuratorTrailerWarranties",
   })

   const formik = useFormik({
      enableReinitialze: true,
      initialValues: {
         ...initialValues,
      },
      validationSchema: validationSchema,
      onSubmit: values => {
         handleSubmit(values)
      },
   })

   const handleInitManage = obj => {
      setEventsType(prev => ({ ...prev, isManage: true }))
      setSelection(obj)
   }

   const handleInitViewDetails = obj => {
      setEventsType(prev => ({
         ...prev,
         isViewDetails: true,
      }))

      setSelection(obj)
   }

   const handleSelectStatusOption = option => {
      setStatusFilters(option)
   }

   const handleStatusChange = async (currStatus, id) => {
      let status =
         currStatus === ConfiguratorPackagesStatus.Active
            ? ConfiguratorPackagesStatus.Inactive
            : ConfiguratorPackagesStatus.Active
      let dataClone = [...data]
      dataClone.map(el => {
         if (el?.id == id) el.status = status
      })
      setData(dataClone)
      let payload = {
         status: status,
      }
      try {
         setTableDataChangeLoading(true)
         let res = await UpdateWarranty(id, payload)
         toast.success("Warranty status has been updated !")
      } catch (err) {
         let dataClone = [...data]
         dataClone.map(el => {
            if (el?.id == id) el.status = currStatus
         })
         setData(dataClone)
      } finally {
         setTableDataChangeLoading(false)
      }
   }

   const handleInitDelete = obj => {
      setSelection(obj)
      setEventsType(prev => ({
         ...prev,
         isDelete: true,
      }))
   }

   const handleSubmit = values => {
      const trailerTypeIds = Array.isArray(values[FIELDS_NAME.TRAILER_TYPE])
         ? values[FIELDS_NAME.TRAILER_TYPE]
              .map(item => {
                 if (typeof item === "object" && item !== null) {
                    return item.value || item.id || null
                 }
                 return item || null
              })
              .filter(id => id !== null && id !== undefined)
         : []

      let bodyData = {
         name: values[FIELDS_NAME.NAME],
         price: Number(values[FIELDS_NAME.PRICE]),
         year: Number(values[FIELDS_NAME.YEAR]),
         status: values[FIELDS_NAME.STATUS],
         description: values[FIELDS_NAME.DESCRIPTION],
         isDefault: values[FIELDS_NAME.DEFAULT],
         isPopular: values[FIELDS_NAME.IS_POPULAR],
         configuratorTrailerIds: trailerTypeIds,
      }

      if (selection?.id) {
         handleUpdate(bodyData)
      } else {
         handleAdd(bodyData)
      }
   }

   const handleUpdate = async data => {
      const payload = {
         ...data,
      }

      try {
         setIsLoading(true)
         await handleUpdateExecution(selection?.id, payload)
         handleModalClose()
         request()
      } finally {
         setIsLoading(false)
      }
   }
   const handleAdd = async data => {
      try {
         setIsLoading(true)
         let res = await CreateWarranty(data)
         toast.success("Warranty has been added successfully !")
         request()
         handleModalClose()
      } catch (error) {
      } finally {
         setIsLoading(false)
      }
   }

   const handleModalClose = () => {
      setSelection({})
      setEventsType(prev => ({
         ...prev,
         isManage: false,
      }))
      formik.resetForm()
   }

   return (
      <>
         <Link to={APP_ROUTES.CONFIGURATOR_TRAILERS}>
            <div className="d-flex align-items-center gap-2 configurator-top-link">
               <span className="configurator-global-arrow">&lt;</span>{" "}
               Configurator Settings
            </div>
         </Link>
         <div className="mb-3 trailer-container">
            <div className="d-flex align-items-center justify-content-between admin-inventory-management-general-inventory-responsiveness trailer-btn-container">
               <h3 className="configurator-global-heading">Warranty</h3>
               <div />

               <Button
                  className={`header-button headerButtonResponsive`}
                  title="Add Warranty"
                  onClick={() => handleInitManage({})}
               />
            </div>
         </div>

         <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between admin-inventory-management-general-inventory-responsiveness ">
               <div className="status-search-con wh-im-dpgi-status  me-2 ">
                  <AutoComplete
                     value={statusFilters}
                     isClearable
                     onChange={val => handleSelectStatusOption(val)}
                     isSearchable={false}
                     placeholder="Select status"
                     customStyles={autoCompleteStyles.STYLE_SIX}
                     classNamePrefix="status-header-search-admin"
                     options={convertToSelectOptions(
                        ConfiguratorPackagesStatus
                     )}
                  />
               </div>
               <div className="ad-location-search-box">
                  <SearchBox
                     inputClasses="vendor-search-input input-search"
                     labelClass="d-none"
                     placeholder="Search"
                     searchIcon={true}
                     searchIconClass="vendor-table-search-icon"
                     conClass="vendor-table-search-inner-class"
                     searchText={searchText}
                     onChange={handleSearchChange}
                  />
               </div>
            </div>
         </div>

         <Row>
            <Col md={12}>
               <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="global-warranties">
                     {provided => (
                        <div
                           {...provided.droppableProps}
                           ref={provided.innerRef}
                        >
               <DataTable
                  isDraggable={!statusFilters?.value && !searchText?.trim()}
                  droppablePlaceholder={provided.placeholder}
                  data={data}
                  tableClasses="package-table"
                  loading={isFetching}
                  config={[
                     {
                        title: COLUMNS.WARRANTY_NAME,
                        className: "",
                        render: data => {
                           return <TruncatedTextWithTooltip text={data.name} />
                        },
                     },

                     {
                        title: COLUMNS.WARRANTY_PRICE,
                        className: "",
                        render: data =>
                           data?.price
                              ? `$${Number(data?.price).toFixed(2)}`
                              : "$0.00",
                     },

                     {
                        title: COLUMNS.WARRANTY_YEAR,
                        className: "",
                        render: data => data.year || "-",
                     },

                     {
                        title: COLUMNS.STATUS,
                        className: "",
                        render: data => (
                           <ActiveInactiveSwitch
                              isDisable={tableDataChangeLoading}
                              id={data.id}
                              name={`${data.id}`}
                              onChange={e =>
                                 handleStatusChange(data?.status, data?.id)
                              }
                              isChecked={
                                 data?.status ==
                                 ConfiguratorPackagesStatus.Active
                              }
                           />
                        ),
                     },
                     {
                        title: COLUMNS.POPULAR_WARRANTY,
                        className: "",
                        render: data =>
                           data?.isPopular
                              ? TOGGLE_STATUS.Yes
                              : TOGGLE_STATUS.No,
                     },
                     {
                        title: COLUMNS.USED_IN,
                        className: "",
                        render: renderUsedIn,
                     },
                     {
                        title: COLUMNS.DEFAULT,
                        className: "",
                        render: data =>
                           data?.isDefault
                              ? TOGGLE_STATUS.Yes
                              : TOGGLE_STATUS.No,
                     },

                     {
                        title: COLUMNS.ACTIONS,

                        render: data => (
                           <div className="d-flex gap-2 pointer">
                              <img
                                 src={eyeIcon}
                                 onClick={() => handleInitViewDetails(data)}
                              />

                              <img
                                 src={editIcon}
                                 onClick={() => handleInitManage(data)}
                              />

                              <img
                                 src={DeleteIcon}
                                 onClick={() => handleInitDelete(data)}
                              />
                           </div>
                        ),
                     },
                  ]}
               />
                        </div>
                     )}
                  </Droppable>
               </DragDropContext>
            </Col>
         </Row>

         {eventsType.isViewDetails && (
            <GlobalWarrantyDetails
               isOpen={eventsType.isViewDetails}
               id={selection?.id}
               onToggleModal={() => {
                  setSelection({})
                  formik.resetForm()
                  setEventsType(prev => ({
                     ...prev,
                     isViewDetails: false,
                  }))
               }}
               formik={formik}
               isLoading={isLoading}
            />
         )}
         {eventsType.isDelete && (
            <DeleteModal
               isOpen={eventsType.isDelete}
               toggleFunction={() => {
                  formik.resetForm()
                  setEventsType(prev => ({ ...prev, isDelete: false }))
               }}
               id={selection?.id}
               apiFunction={DeleteWarranty}
               title={"Warranty"}
               requestFunction={() => {
                  setSelection(null)
                  request()
               }}
            />
         )}

         {eventsType.isManage && (
            <ManageGlobalWarranties
               isLoading={isLoading}
               isOpen={eventsType.isManage}
               isDetailView={false}
               id={selection?.id}
               onToggleModal={() => {
                  setSelection({})
                  setEventsType(prev => ({
                     ...prev,
                     isManage: false,
                  }))
               }}
               formik={formik}
            />
         )}
      </>
   )
}

export default GlobalWarranty
